/**
 * listingDeleteAgent.js — a seller deleting a draft or withdrawn listing (all
 * states; legal copy §15.3 in docs/legal/states/WI/contingency-notices-copy.v1.md).
 *
 * Contract (backend/app/routes/houses.py):
 *   GET    /api/houses/{id}/delete-preview
 *          → { house_id, allowed, refusal: {code, copy_key, message}|null, mode,
 *              property_address, open_offer_count, countered_offer_count, tour_count,
 *              listing_agreement_signed, confirm_copy_keys, copy_params }
 *          (backend/app/services/listing_deletion.py preview)
 *          What the confirmation must tell the seller: the pending or
 *          countered offers that will be closed, a counter-offer that will be
 *          withdrawn, the tours that will be cancelled, and whether a seller
 *          listing agreement was signed. Reads only.
 *   DELETE /api/houses/{id}
 *          → { deleted: true, mode: 'soft'|'hard', house_id, closed_offer_ids, voided_document_ids,
 *              cancelled_tour_ids, notified_buyer_ids, ..., message, copy_key }
 *          403 not the listing's seller; 404; 409 `{code, message, copy_key}`:
 *            listing_not_off_market (GEN.error.listing_delete_not_off_market)
 *            listing_pending_or_sold (GEN.error.listing_delete_has_transaction)
 *            listing_has_transaction | listing_has_accepted_offer
 *                                   (GEN.error.listing_delete_has_transaction)
 *            listing_has_signed_documents (GEN.error.listing_delete_signed_documents)
 *            notice_evidence_retained (WI.error.delete_blocked_by_notices; fallback)
 *            listing_delete_changed (GEN.error.listing_delete_changed: a buyer
 *                                    accepted a counter-offer before the lock)
 *
 * All calls go through APIAgent (cookie auth, structured `{code, message,
 * copy_key}` errors on `err.detail`).
 */
import APIAgent from './apiAgent';

/** Listing statuses a seller can delete (copy §15.3 rule 1). Active must be withdrawn first. */
export const DELETABLE_STATUSES = ['draft', 'withdrawn'];

export const canDeleteListing = (house) => DELETABLE_STATUSES.includes(house?.listing_status);

/** The 409 code for a listing that changed while the seller was deleting it (copy §15.3 rule 3). */
export const LISTING_DELETE_CHANGED = 'listing_delete_changed';

const count = (...values) => {
  const n = values.map(Number).find((v) => Number.isFinite(v) && v >= 0);
  return n === undefined ? 0 : Math.floor(n);
};

/**
 * The preview in the shape the dialog uses, tolerant of a missing field (0 / false).
 *
 * @param {object} raw - The `GET …/delete-preview` body.
 * @returns {{allowed: boolean, refusal: (object|null), propertyAddress: (string|null),
 *   openOfferCount: number, hasCounter: boolean, tourCount: number, listingAgreementSigned: boolean}}
 */
export function normalizeDeletePreview(raw = {}) {
  const r = raw || {};
  const countered = count(r.countered_offer_count, r.counter_count);
  return {
    allowed: r.allowed !== false,
    refusal: r.allowed === false ? (r.refusal || {}) : null,
    propertyAddress: typeof r.property_address === 'string' && r.property_address.trim() ? r.property_address.trim() : null,
    openOfferCount: count(r.open_offer_count, r.pending_offer_count),
    hasCounter: Boolean(r.has_counter) || countered > 0,
    tourCount: count(r.tour_count, r.pending_tour_count),
    listingAgreementSigned: Boolean(r.listing_agreement_signed),
  };
}

/** `GET /api/houses/{id}/delete-preview`, normalized. */
export async function getListingDeletePreview(houseId) {
  const raw = await APIAgent.request(`/api/houses/${encodeURIComponent(houseId)}/delete-preview`, 'GET');
  return normalizeDeletePreview(raw);
}

/** `DELETE /api/houses/{id}`. */
export function deleteListing(houseId) {
  return APIAgent.request(`/api/houses/${encodeURIComponent(houseId)}`, 'DELETE');
}

const listingDeleteAgent = { getListingDeletePreview, deleteListing };

export default listingDeleteAgent;
